import {Cell} from "@tanstack/react-table";
import {useState} from "react";
import {Link} from "react-router";

import {ChecksModalButton} from "@/components/ChecksModalButton/ChecksModalButton.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {StatusPill} from "@/components/statusPill/StatusPill.tsx";
import {EmptyTable} from "@/components/table/subComponents/EmptyTable/EmptyTable.tsx";
import {LimitAndOffset} from "@/components/table/subComponents/Paginator/Paginator.tsx";
import {Table} from "@/components/table/Table.tsx";
import {ColumnSpec, Sorting, SortingDirection} from "@/components/table/Table.types.ts";
import {Tooltip} from "@/components/toolTip/Tooltip.tsx";
import {shortDateUser} from "@/lib/format.tsx";
import {PRIVATE_LOCATION_SUPPORT_URL} from "@/lib/links.ts";
import {PrivateLocationGlobalStatusCodeMap, PrivateLocationWithGlobalStatus} from "@/lib/types/monitoring.ts";
import {DeployPrivateLocationButton} from "@/pages/monitoring/privatelocations/list/DeployPrivateLocationButton.tsx";
import {PrivateLocationActions} from "@/pages/monitoring/privatelocations/list/tableComponents/actions/PrivateLocationActions.tsx";
import {APITokenPopup} from "@/pages/monitoring/privatelocations/list/tableComponents/APITokenPopup.tsx";
import {PlatformDropdown} from "@/pages/monitoring/privatelocations/list/tableComponents/deployAssistant/PlatformDropdown.tsx";
import {ExtendStatusModal} from "@/pages/monitoring/privatelocations/list/tableComponents/ExtendedStatusModal.tsx";
import {LastStatusReportCell} from "@/pages/monitoring/privatelocations/list/tableComponents/LastStatusReportCell.tsx";
import {LoadAverageCell} from "@/pages/monitoring/privatelocations/list/tableComponents/LoadAverageCell.tsx";

import styles from "./PrivateLocationsTable.module.scss";

type PrivateLocationsTableProps = {
  privateLocations: PrivateLocationWithGlobalStatus[];
  privateLocationsAvailable: number;
  sortColumn: string;
  sortDirection: SortingDirection;
  onSortingChange?: (sorting: Sorting) => void;
  loading?: boolean;
  pagination?: LimitAndOffset;
  totalRows?: number;
  updatePagination?: (laf: LimitAndOffset) => void;
  paginationData?: unknown[];
};

export const PrivateLocationsTable = ({
  privateLocations,
  privateLocationsAvailable,
  sortColumn,
  sortDirection,
  onSortingChange,
  loading,
  pagination,
  totalRows,
  updatePagination,
  paginationData,
}: PrivateLocationsTableProps) => {
  const {user} = useCurrentUserContext();

  const [showExtendedStatus, setShowExtendedStatus] = useState(false);
  const [extendedStatusLocation, setExtendedStatusLocation] = useState<PrivateLocationWithGlobalStatus | undefined>(
    undefined
  );

  const onClickExtendedStatus = (
    _: React.MouseEvent,
    cell: Cell<PrivateLocationWithGlobalStatus, unknown> | undefined
  ) => {
    if (cell) {
      setExtendedStatusLocation(cell.row.original);
      setShowExtendedStatus(true);
    }
  };

  const specs: ColumnSpec<PrivateLocationWithGlobalStatus>[] = [
    ...[
      {
        fieldName: "name",
        fieldTitle: "Name",
        enableSorting: true,
        meta: {
          onClick: onClickExtendedStatus,
          headerClassName: styles.nameColumn,
        },
        accessor: (location: PrivateLocationWithGlobalStatus) => (
          <Tooltip tooltipContent={`${location.name}`} delayMs={500}>
            <div className="ellipsis-overflow">{location.name}</div>
          </Tooltip>
        ),
      },
      {
        fieldName: "status",
        fieldTitle: "Status",
        meta: {
          onClick: onClickExtendedStatus,
          headerClassName: styles.statusColumn,
        },
        accessor: (location: PrivateLocationWithGlobalStatus) => (
          <StatusPill
            type={"incident"}
            status={PrivateLocationGlobalStatusCodeMap[location.globalStatusCode]}
            variant={"textAndIcon"}
          />
        ),
      },
      {
        fieldName: "created_at",
        fieldTitle: "Provision Date",
        meta: {
          onClick: onClickExtendedStatus,
          headerClassName: styles.createdAtColumn,
        },
        accessor: (location: PrivateLocationWithGlobalStatus) => shortDateUser(location.created_at, user!),
      },
      {
        fieldName: "version",
        fieldTitle: "Version",
        meta: {
          onClick: onClickExtendedStatus,
          headerClassName: styles.versionColumn,
        },
        accessor: (location: PrivateLocationWithGlobalStatus) => location.version,
      },
      {
        fieldName: "checks",
        fieldTitle: "Checks",
        meta: {
          headerClassName: styles.checksColumn,
        },
        accessor: (location: PrivateLocationWithGlobalStatus) => {
          return (
            <ChecksModalButton
              id={location.id}
              numberOfChecks={location.num_services}
              model_type={"MonitoringServer"}
              title={"Private Location: " + location.name}
            >
              {location.num_services}
            </ChecksModalButton>
          );
        },
      },
      {
        fieldName: "load_average",
        fieldTitle: "Load Average",
        meta: {
          onClick: onClickExtendedStatus,
          headerClassName: styles.loadAverageColumn,
        },
        accessor: (location: PrivateLocationWithGlobalStatus) => <LoadAverageCell status={location.status} />,
      },
      {
        fieldName: "last_report",
        fieldTitle: "Last Report",
        meta: {
          onClick: onClickExtendedStatus,
          headerClassName: styles.lastReportColumn,
        },
        accessor: (location: PrivateLocationWithGlobalStatus) => <LastStatusReportCell status={location.status} />,
      },
    ],
    ...(user!.has_admin_access
      ? [
          {
            fieldName: "api_token",
            fieldTitle: "API Token",
            meta: {
              headerClassName: styles.apiTokenColumn,
            },
            accessor: (location: PrivateLocationWithGlobalStatus) => <APITokenPopup apiToken={location.api_token} />,
          },
        ]
      : []),
    ...[
      {
        fieldName: "download",
        fieldTitle: "Download",
        meta: {
          headerClassName: styles.downloadColumn,
        },
        accessor: (location: PrivateLocationWithGlobalStatus) => <PlatformDropdown privateLocationData={location} />,
      },
    ],

    ...(user!.has_admin_access
      ? [
          {
            fieldName: "actions",
            meta: {
              headerClassName: styles.actionsColumn,
            },
            accessor: (location: PrivateLocationWithGlobalStatus) => <PrivateLocationActions location={location} />,
          },
        ]
      : []),
  ];

  return (
    <>
      <Table
        rows={privateLocations}
        sorting={{column: sortColumn, direction: sortDirection}}
        onSortingChange={onSortingChange}
        tableRowClassName="cursor-pointer"
        isLoading={loading}
        specs={specs}
        initialState={{columnPinning: {right: ["actions"]}}}
        fixedWidth
        minWidth={"90rem"}
        pagination={pagination}
        totalRows={totalRows}
        updatePagination={updatePagination}
        paginationData={paginationData}
        noDataComponent={
          <EmptyTable colSpan={specs.length}>
            <div className={"m-4 d-flex flex-column align-items-center justify-content-center gap-2"}>
              {privateLocationsAvailable === 0 && (
                <div className={"pulseTextBodyBold"}>You need to Upgrade to deploy Private Locations.</div>
              )}
              <Link className={"pulseTextBody"} to={PRIVATE_LOCATION_SUPPORT_URL}>
                Learn how to get started
              </Link>
              <DeployPrivateLocationButton
                privateLocationsAvailable={privateLocationsAvailable}
                variant={"secondary"}
              />
            </div>
          </EmptyTable>
        }
      />
      {extendedStatusLocation && (
        <ExtendStatusModal
          location={extendedStatusLocation}
          show={showExtendedStatus}
          hide={() => setShowExtendedStatus(false)}
        />
      )}
    </>
  );
};
